
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GoldChart, SilverChart, ChartLoadingState, MetalStatsCards } from "@/components/charts";
import { MetalPrice, TimePeriod } from "@/lib/metal-price";

interface ChartTabsProps {
  isLoading: boolean;
  error: boolean;
  noData: boolean;
  goldPriceHistory: MetalPrice[];
  silverPriceHistory: MetalPrice[];
  selectedTimePeriod: TimePeriod;
  handleRetryLoading: () => void;
  getPeriodText: (period: TimePeriod) => string;
  isSearchingAlternative?: boolean;
}

export function ChartTabs({
  isLoading,
  error,
  noData,
  goldPriceHistory,
  silverPriceHistory,
  selectedTimePeriod,
  handleRetryLoading,
  getPeriodText,
  isSearchingAlternative = false
}: ChartTabsProps) {
  const safeGoldHistory = goldPriceHistory.filter((p): p is MetalPrice => p?.price != null && p?.timestamp != null);
  const safeSilverHistory = silverPriceHistory.filter((p): p is MetalPrice => p?.price != null && p?.timestamp != null);

  return (
    <Tabs defaultValue="gold" className="mb-10">
      <TabsList className="grid w-full grid-cols-2 mb-3">
        <TabsTrigger value="gold" className="text-base font-medium py-3 data-[state=active]:text-gold data-[state=active]:shadow-gold/10">
          Oro
        </TabsTrigger>
        <TabsTrigger value="silver" className="text-base font-medium py-3 data-[state=active]:text-silver data-[state=active]:shadow-silver/10">
          Argento
        </TabsTrigger>
      </TabsList>
      <TabsContent value="gold">
        {isLoading ? (
          <ChartLoadingState isSearchingAlternative={isSearchingAlternative} period={getPeriodText(selectedTimePeriod)} />
        ) : error ? (
          <ChartLoadingState 
            error={true} 
            errorMessage="Impossibile caricare i dati dell'oro" 
            onRetry={handleRetryLoading}
          />
        ) : noData || safeGoldHistory.length === 0 ? (
          <ChartLoadingState 
            noData={true} 
            period={getPeriodText(selectedTimePeriod)}
            onRetry={handleRetryLoading}
          />
        ) : (
          <>
            <GoldChart 
              goldPriceHistory={safeGoldHistory}
              selectedTimePeriod={selectedTimePeriod}
            />
            <MetalStatsCards 
              data={safeGoldHistory}
              metalType="gold"
              isLoading={isLoading}
            />
          </>
        )}
      </TabsContent>
      <TabsContent value="silver">
        {isLoading ? (
          <ChartLoadingState isSearchingAlternative={isSearchingAlternative} period={getPeriodText(selectedTimePeriod)} />
        ) : error ? (
          <ChartLoadingState 
            error={true} 
            errorMessage="Impossibile caricare i dati dell'argento"
            onRetry={handleRetryLoading}
          />
        ) : noData || safeSilverHistory.length === 0 ? (
          <ChartLoadingState 
            noData={true} 
            period={getPeriodText(selectedTimePeriod)}
            onRetry={handleRetryLoading}
          />
        ) : (
          <>
            <SilverChart 
              silverPriceHistory={safeSilverHistory}
              selectedTimePeriod={selectedTimePeriod}
            />
            <MetalStatsCards 
              data={safeSilverHistory}
              metalType="silver"
              isLoading={isLoading}
            />
          </>
        )}
      </TabsContent>
    </Tabs>
  );
}
